import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DollarSign,
  TrendingUp,
  Users,
  Database,
  MessageSquare,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ArrowUpCircle,
  ArrowDownCircle,
  Calendar,
  Receipt,
  CreditCard,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'
import { Progress } from '@/components/ui/progress'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { createReceipt, printReceipt, downloadReceipt } from '@/lib/invoiceService'
import { EmailService } from '@/lib/emailService'

interface SubscriptionSummary {
  church_id: string
  church_name: string
  plan: 'starter' | 'growth' | 'leadership'
  status: string
  annual_price: number
  max_members: number
  max_admins: number
  max_storage_gb: number
  max_sms_monthly: number
  subscription_expires_at: string | null
  finance_enabled: boolean
  attendance_enabled: boolean
  hr_payroll_enabled: boolean
  current_members_count: number
  current_admins_count: number
  current_storage_mb: number
  current_month_sms_sent: number
  members_usage_percent: number
  storage_usage_percent: number
  sms_usage_percent: number
  days_until_expiry: number | null
}

interface RevenueByPlan {
  plan: string
  church_count: number
  total_annual_revenue: number
  avg_annual_price: number
}

interface SubscriptionManagementProps {
  user: AdminUser | null
}

export default function SubscriptionManagement({ user }: SubscriptionManagementProps) {
  const [loading, setLoading] = useState(true)
  const [subscriptions, setSubscriptions] = useState<SubscriptionSummary[]>([])
  const [revenueData, setRevenueData] = useState<RevenueByPlan[]>([])
  const [selectedChurch, setSelectedChurch] = useState<SubscriptionSummary | null>(null)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'growth' | 'leadership'>('starter')
  const [upgrading, setUpgrading] = useState(false)

  // Payment recording state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false)
  const [recordingPayment, setRecordingPayment] = useState(false)
  const [paymentData, setPaymentData] = useState({
    amount: '',
    currency: 'GHS' as 'USD' | 'GHS' | 'NGN' | 'KES' | 'UGX' | 'ZAR' | 'EUR' | 'GBP',
    paymentMethod: 'check' as 'check' | 'bank_transfer' | 'cash' | 'mobile_money' | 'other',
    transactionReference: '',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
  })

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to access subscription management.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    loadSubscriptionData()
  }, [])

  async function loadSubscriptionData() {
    try {
      setLoading(true)

      // Load subscription summary
      const { data: subsData, error: subsError } = await supabase
        .from('subscription_summary')
        .select('*')
        .order('annual_price', { ascending: false })

      if (subsError) throw subsError

      setSubscriptions(subsData || [])

      // Load revenue summary
      const { data: revenueData, error: revenueError } = await supabase
        .from('subscription_revenue_summary')
        .select('*')

      if (revenueError) throw revenueError

      setRevenueData(revenueData || [])
    } catch (error) {
      console.error('Error loading subscription data:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleUpgradeSubscription() {
    if (!selectedChurch) return

    try {
      setUpgrading(true)

      const { error } = await supabase.rpc('upgrade_church_subscription', {
        p_church_id: selectedChurch.church_id,
        p_plan: selectedPlan,
        p_billing_cycle: 'annual',
        p_payment_method: 'manual_admin',
      })

      if (error) throw error

      // Reload data
      await loadSubscriptionData()

      setShowUpgradeDialog(false)
      setSelectedChurch(null)
    } catch (error) {
      console.error('Error upgrading subscription:', error)
      alert('Failed to update subscription. Please try again.')
    } finally {
      setUpgrading(false)
    }
  }

  function openUpgradeDialog(church: SubscriptionSummary) {
    setSelectedChurch(church)
    setSelectedPlan(church.plan)
    setShowUpgradeDialog(true)
  }

  function openPaymentDialog(church: SubscriptionSummary) {
    setSelectedChurch(church)
    setPaymentData({
      amount: church.annual_price.toString(),
      currency: 'GHS',
      paymentMethod: 'check',
      transactionReference: '',
      paymentDate: new Date().toISOString().split('T')[0],
      notes: '',
    })
    setShowPaymentDialog(true)
  }

  async function handleRecordPayment() {
    if (!selectedChurch) return

    try {
      setRecordingPayment(true)

      const amount = parseFloat(paymentData.amount)
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid payment amount')
        return
      }

      // Record payment in database
      const { error: paymentError } = await supabase
        .from('subscription_payments')
        .insert({
          church_id: selectedChurch.church_id,
          amount: amount,
          currency: paymentData.currency,
          payment_method: paymentData.paymentMethod,
          transaction_reference: paymentData.transactionReference || `MANUAL-${Date.now()}`,
          payment_date: paymentData.paymentDate,
          status: 'completed',
          notes: paymentData.notes,
          recorded_by: user?.id,
        })

      if (paymentError) throw paymentError

      // Update subscription status to active and extend expiration
      const { error: updateError } = await supabase
        .from('church_subscriptions')
        .update({
          status: 'active',
          last_payment_date: paymentData.paymentDate,
          subscription_expires_at: new Date(
            new Date(paymentData.paymentDate).getTime() + 365 * 24 * 60 * 60 * 1000
          ).toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('church_id', selectedChurch.church_id)

      if (updateError) throw updateError

      // Generate and print receipt
      const receipt = await createReceipt(
        selectedChurch.church_id,
        paymentData.paymentMethod,
        paymentData.transactionReference || `MANUAL-${Date.now()}`,
        paymentData.paymentDate
      )

      if (receipt) {
        printReceipt(receipt)
      }

      // Get church admin details for email notification
      const { data: churchAdminData } = await supabase
        .from('profiles')
        .select('email, first_name, last_name')
        .eq('church_id', selectedChurch.church_id)
        .eq('admin_level', 'church_owner')
        .single()

      // Send payment confirmation email
      if (churchAdminData && receipt) {
        try {
          await EmailService.sendPaymentConfirmation({
            churchName: selectedChurch.church_name,
            churchEmail: churchAdminData.email,
            adminName: `${churchAdminData.first_name} ${churchAdminData.last_name}`,
            amount: amount,
            currency: paymentData.currency,
            paymentMethod: paymentData.paymentMethod,
            transactionReference: paymentData.transactionReference || `MANUAL-${Date.now()}`,
            paymentDate: paymentData.paymentDate,
            receiptNumber: receipt.receiptNumber,
          })
          console.log('Payment confirmation email sent successfully')
        } catch (emailError) {
          console.error('Failed to send payment confirmation email:', emailError)
          // Don't fail the whole operation if email fails
        }
      }

      alert('Payment recorded successfully! Receipt has been generated and confirmation email sent.')

      // Reload data and close dialog
      await loadSubscriptionData()
      setShowPaymentDialog(false)
      setSelectedChurch(null)
    } catch (error: any) {
      console.error('Error recording payment:', error)
      alert('Failed to record payment: ' + (error.message || 'Unknown error'))
    } finally {
      setRecordingPayment(false)
    }
  }

  function getStatusBadge(status: string) {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>
      case 'trial':
        return <Badge className="bg-blue-500"><Clock className="h-3 w-3 mr-1" />Trial</Badge>
      case 'past_due':
        return <Badge className="bg-yellow-500"><AlertCircle className="h-3 w-3 mr-1" />Past Due</Badge>
      case 'cancelled':
        return <Badge className="bg-gray-500"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>
      case 'expired':
        return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" />Expired</Badge>
      default:
        return <Badge variant="outline">Unknown</Badge>
    }
  }

  function getPlanBadge(plan: string) {
    switch (plan) {
      case 'leadership':
        return <Badge className="bg-sky-500">Leadership</Badge>
      case 'growth':
        return <Badge className="bg-blue-500">Growth</Badge>
      case 'starter':
        return <Badge variant="outline">Starter</Badge>
      default:
        return <Badge variant="outline">{plan}</Badge>
    }
  }

  function getUsageColor(percent: number): string {
    if (percent >= 90) return 'bg-red-500'
    if (percent >= 75) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  // Calculate totals
  const totalRevenue = revenueData.reduce((sum, r) => sum + r.total_annual_revenue, 0)
  const totalChurches = subscriptions.length
  const paidChurches = subscriptions.filter(s => s.plan !== 'starter').length
  const avgRevenue = paidChurches > 0 ? totalRevenue / paidChurches : 0

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading subscription data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Subscription Management</h1>
        <p className="text-muted-foreground">Manage church subscriptions, plans, and usage</p>
      </div>

      {/* Revenue Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Annual Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">GHS {totalRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              GHS {(totalRevenue / 12).toFixed(0)}/month MRR
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid Subscriptions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{paidChurches}</div>
            <p className="text-xs text-muted-foreground">
              {totalChurches} total churches
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Revenue Per Church</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">GHS {avgRevenue.toFixed(0)}</div>
            <p className="text-xs text-muted-foreground">
              GHS {(avgRevenue / 12).toFixed(0)}/month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Free-to-Paid Rate</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalChurches > 0 ? ((paidChurches / totalChurches) * 100).toFixed(1) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Conversion rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Plan Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue by Plan</CardTitle>
          <CardDescription>Breakdown of subscriptions and revenue by tier</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {revenueData.map((plan) => (
              <div key={plan.plan} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getPlanBadge(plan.plan)}
                  <div>
                    <div className="font-medium">{plan.church_count} churches</div>
                    <div className="text-sm text-muted-foreground">
                      Avg: GHS {plan.avg_annual_price.toFixed(0)}/year
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold">GHS {plan.total_annual_revenue.toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground">
                    GHS {(plan.total_annual_revenue / 12).toFixed(0)}/month
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Church Subscriptions</CardTitle>
          <CardDescription>View and manage subscription details and usage</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Church</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Members Usage</TableHead>
                <TableHead>Storage Usage</TableHead>
                <TableHead>SMS Usage</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No subscriptions found.
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map((sub) => (
                  <TableRow key={sub.church_id}>
                    <TableCell>
                      <div className="font-medium">{sub.church_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {sub.finance_enabled ? '💰 ' : ''}
                        {sub.attendance_enabled ? '📊 ' : ''}
                        {sub.hr_payroll_enabled ? '👥 ' : ''}
                      </div>
                    </TableCell>
                    <TableCell>{getPlanBadge(sub.plan)}</TableCell>
                    <TableCell>{getStatusBadge(sub.status)}</TableCell>
                    <TableCell>
                      <div className="font-medium">GHS {sub.annual_price}</div>
                      <div className="text-xs text-muted-foreground">/year</div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Progress
                            value={sub.members_usage_percent || 0}
                            className="w-16 h-2"
                          />
                          <span className="text-xs font-medium">
                            {sub.members_usage_percent?.toFixed(0) || 0}%
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {sub.current_members_count || 0} / {sub.max_members === 999999 ? '∞' : sub.max_members}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Progress
                            value={sub.storage_usage_percent || 0}
                            className="w-16 h-2"
                          />
                          <span className="text-xs font-medium">
                            {sub.storage_usage_percent?.toFixed(0) || 0}%
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {(sub.current_storage_mb / 1024).toFixed(1)}GB / {sub.max_storage_gb === 999999 ? '∞' : sub.max_storage_gb}GB
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Progress
                            value={sub.sms_usage_percent || 0}
                            className="w-16 h-2"
                          />
                          <span className="text-xs font-medium">
                            {sub.sms_usage_percent?.toFixed(0) || 0}%
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {sub.current_month_sms_sent || 0} / {sub.max_sms_monthly}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {sub.subscription_expires_at ? (
                        <div>
                          <div className="text-sm font-medium">
                            {sub.days_until_expiry !== null && sub.days_until_expiry > 0
                              ? `${sub.days_until_expiry} days`
                              : 'Expired'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(sub.subscription_expires_at).toLocaleDateString()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">Never</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openUpgradeDialog(sub)}
                        >
                          {sub.plan === 'starter' ? (
                            <><ArrowUpCircle className="h-4 w-4 mr-1" />Upgrade</>
                          ) : (
                            <>Change Plan</>
                          )}
                        </Button>
                        {sub.plan !== 'starter' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPaymentDialog(sub)}
                          >
                            <Receipt className="h-4 w-4 mr-1" />
                            Record Payment
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payment Recording Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Record Manual Payment
            </DialogTitle>
            <DialogDescription>
              Record check, bank transfer, or cash payment for {selectedChurch?.church_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Payment Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  placeholder="300.00"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Currency *</Label>
                <Select
                  value={paymentData.currency}
                  onValueChange={(value: any) => setPaymentData({ ...paymentData, currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">🇺🇸 USD - US Dollar</SelectItem>
                    <SelectItem value="GHS">🇬🇭 GHS - Ghana Cedi</SelectItem>
                    <SelectItem value="NGN">🇳🇬 NGN - Nigerian Naira</SelectItem>
                    <SelectItem value="KES">🇰🇪 KES - Kenyan Shilling</SelectItem>
                    <SelectItem value="UGX">🇺🇬 UGX - Ugandan Shilling</SelectItem>
                    <SelectItem value="ZAR">🇿🇦 ZAR - South African Rand</SelectItem>
                    <SelectItem value="EUR">🇪🇺 EUR - Euro</SelectItem>
                    <SelectItem value="GBP">🇬🇧 GBP - British Pound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Plan: {selectedChurch && getPlanBadge(selectedChurch.plan)} - GHS {selectedChurch?.annual_price}/year
            </p>

            <div className="space-y-2">
              <Label htmlFor="paymentMethod">Payment Method *</Label>
              <Select
                value={paymentData.paymentMethod}
                onValueChange={(value: any) => setPaymentData({ ...paymentData, paymentMethod: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transactionReference">Transaction Reference</Label>
              <Input
                id="transactionReference"
                value={paymentData.transactionReference}
                onChange={(e) => setPaymentData({ ...paymentData, transactionReference: e.target.value })}
                placeholder="Check number, transfer ID, etc."
              />
              <p className="text-xs text-muted-foreground">
                e.g., Check #1234, Wire Transfer #ABC123
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentDate">Payment Date *</Label>
              <Input
                id="paymentDate"
                type="date"
                value={paymentData.paymentDate}
                onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder="Any additional information about this payment..."
                rows={3}
              />
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">What happens next:</p>
                  <ul className="text-xs space-y-1 text-blue-800 dark:text-blue-200 mt-1">
                    <li>• Subscription status will be set to "Active"</li>
                    <li>• Expiration date will be extended by 1 year</li>
                    <li>• A receipt will be generated and can be printed</li>
                    <li>• Payment will be logged in the audit trail</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)} disabled={recordingPayment}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} disabled={recordingPayment || !paymentData.amount}>
              {recordingPayment ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Recording...</>
              ) : (
                <><Receipt className="h-4 w-4 mr-2" />Record & Generate Receipt</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Subscription Plan</DialogTitle>
            <DialogDescription>
              Update the subscription plan for {selectedChurch?.church_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Current Plan</label>
              <div className="flex items-center gap-2">
                {selectedChurch && getPlanBadge(selectedChurch.plan)}
                <span className="text-sm text-muted-foreground">
                  GHS {selectedChurch?.annual_price}/year
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">New Plan</label>
              <Select value={selectedPlan} onValueChange={(value: any) => setSelectedPlan(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">
                    <div className="flex items-center justify-between w-full">
                      <span>Starter</span>
                      <span className="text-muted-foreground ml-4">GHS 199/month</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="growth">
                    <div className="flex items-center justify-between w-full">
                      <span>Growth</span>
                      <span className="text-muted-foreground ml-4">GHS 499/month</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="leadership">
                    <div className="flex items-center justify-between w-full">
                      <span>Leadership</span>
                      <span className="text-muted-foreground ml-4">GHS 1,499/month</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedPlan === 'growth' && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm">
                <div className="font-medium mb-1">Growth Plan Includes:</div>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li>• Up to 2,000 members</li>
                  <li>• Finance, Attendance, Advanced Ministry</li>
                  <li>• 50GB storage, 3,000 SMS/month</li>
                </ul>
              </div>
            )}

            {selectedPlan === 'leadership' && (
              <div className="p-3 bg-sky-50 dark:bg-sky-950 rounded-lg text-sm">
                <div className="font-medium mb-1">Leadership Plan Includes:</div>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li>• Unlimited members, storage, locations</li>
                  <li>• Everything in Growth</li>
                  <li>• HR & Payroll, Mobile App, API Access</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)} disabled={upgrading}>
              Cancel
            </Button>
            <Button onClick={handleUpgradeSubscription} disabled={upgrading}>
              {upgrading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating...</>
              ) : (
                <>Update Subscription</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
